"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View2Response } from "@glideapps/custom-component-client";
import iframeResize from "@iframe-resizer/parent";
import chroma from "chroma-js";
import isEqual from "lodash.isequal";
import { SkeletonLoader } from "./SkeletonLoader.js";
import { incomingMessage } from "./iframeProtocol/index.js";
import { isLeft, isRight } from "fp-ts/lib/Either.js";
import { v1OutgoingMessages } from "./iframeProtocol/v1.js";
import { preProtocolOutgoingMessages } from "./iframeProtocol/pre-protocol.js";
import { v2OutgoingMessages } from "./iframeProtocol/v2.js";
import { messagesFromComponent as v3MessagesFromComponent } from "./iframeProtocol/v3.js";
const defaultHost = "//components.glide.info";
const iframeResizerLicense = "o12fh9wqww-hcmhoi3ma4-173stqic5dk";
function useInterval(callback, delay) {
    const savedCallback = useRef();
    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);
    // Set up the interval.
    useEffect(() => {
        function tick() {
            var _a;
            (_a = savedCallback.current) === null || _a === void 0 ? void 0 : _a.call(savedCallback);
        }
        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
const allowed = [
    "camera",
    "microphone",
    "geolocation",
    "payment",
    "clipboard-write",
    "fullscreen",
    "picture-in-picture",
    "autoplay"
];
function makeBackendURL(root, path) {
    let url = root;
    if (!url.endsWith("/"))
        url += "/";
    url += path;
    return url;
}
const MAX_READY_RETRIES = 50;
const defaultTheme = {
    colors: {
        foreground: "#000",
        background: "#fff",
        accent: "#f00"
    }
};
const defaultConfig = {
    colorScheme: "auto",
    cssVariables: { "--color-accent": "#731358" }
};
const colorToHSL = (color) => {
    const [h, s, l] = chroma(color).hsl();
    return `${Math.ceil(h)}deg ${Math.ceil(s * 100)}% ${Math.ceil(l * 100)}%`;
};
const convertCSSColorsToHSL = (cssVariables) => Object.entries(cssVariables).reduce((acc, [key, value]) => {
    acc[key] = key.startsWith("--color")
        ? colorToHSL(value)
        : value;
    return acc;
}, {});
const createHTMLClassName = (colorScheme) => {
    return `color-scheme-${colorScheme}`;
};
const iframeCache = {};
// This component displays the code that is generated by the custom component.
// It also handles the communication with the iframe that is embedded in the code.
export function CustomComponentView(props) {
    var _a, _b, _c, _d, _e, _f;
    const { className, style, component, state = {}, setState, theme = defaultTheme, themeV2 = {}, config = defaultConfig, onActionTriggered, isRunning = false, host = defaultHost, debug = false, disableCache = false, shellHost, actions } = props;
    const log = useCallback((...args) => {
        if (!debug)
            return;
        console.log("[CUSTOM COMPONENT]", ...args);
    }, [debug]);
    const [componentState, setComponentState] = useState("initial");
    const [protocolVersion, setProtocolVersion] = useState(null);
    useEffect(() => {
        log("React componentState", componentState);
    }, [componentState, log]);
    // Create a random iframe ID to avoid conflicts with iframe-resizer
    const iframeId = useMemo(() => `custom-component-${Math.random().toString(36).slice(2)}`, []);
    // We store and display the HTML of the custom component in the iframe.
    // We tried just displaying the view URL directly, but it caused iframe
    // resizer to break.
    const [srcDoc, setSrcDoc] = useState();
    const [pendingStateUpdate, setPendingStateUpdate] = useState(false);
    const iframeRef = useRef(null);
    const send = useCallback((message, validator) => {
        var _a;
        const iframeWindow = (_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow;
        if (!iframeWindow) {
            log("NO IFRAME REF");
            return;
        }
        const result = validator.decode(message);
        if (isLeft(result)) {
            log("INVALID MESSAGE", result.left);
            return;
        }
        const _b = result.right, { type } = _b, data = __rest(_b, ["type"]);
        iframeWindow.postMessage(Object.assign(Object.assign({}, data), { type }), "*");
    }, [log]);
    const sendPreProtocol = useCallback((message) => send(message, preProtocolOutgoingMessages), [send]);
    const sendV1 = useCallback((message) => send(message, v1OutgoingMessages), [send]);
    const sendV2 = useCallback((message) => send(message, v2OutgoingMessages), [send]);
    const sendV3 = useCallback((message) => send(message, v3MessagesFromComponent), [send]);
    const sendSetState = useCallback((state, version = protocolVersion) => {
        switch (version) {
            case "pre-protocol":
                sendPreProtocol({ type: "set-state", state });
                break;
            case "v1":
                sendV1({ type: "glide:set-state", state });
                break;
            case "v2":
                sendV2({ type: "glide:set-state", state });
                break;
            case "v3":
                sendV3({ type: "glide:set-state", state });
                break;
            default:
                log(`Unknown protocol version: ${version}`);
                break;
        }
    }, [log, protocolVersion, sendPreProtocol, sendV1, sendV2, sendV3]);
    const sendSetTheme = useCallback((themePreProtocol, themeV2, version = protocolVersion) => {
        switch (version) {
            case "pre-protocol":
                sendPreProtocol({ type: "set-theme", theme: themePreProtocol });
                break;
            case "v1":
                sendV1({ type: "glide:set-theme", theme: themePreProtocol });
                break;
            case "v2":
                sendV2({ type: "glide:set-theme", theme: themeV2 });
                break;
            case "v3":
                // No longer supported
                break;
            default:
                log(`Unknown protocol version: ${version}`);
                break;
        }
    }, [log, protocolVersion, sendPreProtocol, sendV1, sendV2]);
    const sendSetConfig = useCallback((config, version = protocolVersion) => {
        switch (version) {
            case "pre-protocol":
            case "v1":
            case "v2":
                // Not implemented
                break;
            case "v3":
                sendV3({
                    type: "glide:set-config",
                    config: Object.assign(Object.assign({}, config), { cssVariables: convertCSSColorsToHSL(config.cssVariables) })
                });
                break;
            default:
                log(`Unknown protocol version: ${version}`);
                break;
        }
    }, [log, protocolVersion, sendV3]);
    const boot = useCallback((version) => {
        setProtocolVersion(version);
        setComponentState("ready");
        sendSetState(state, version);
        sendSetTheme(theme, themeV2, version);
        sendSetConfig(config, version);
    }, [config, sendSetConfig, sendSetState, sendSetTheme, state, theme, themeV2]);
    const setStateIfDifferent = useCallback((nextState) => {
        log("iframe sent state update", nextState);
        if (!setState)
            return;
        // Check if the data is actually different.
        if (isEqual(state, nextState))
            return;
        if (pendingStateUpdate) {
            log("Pending state update - skipping", nextState);
            return;
        }
        log("Notifying parent of updated state", nextState);
        // Notify the parent component of the new state.
        setState(nextState);
        setPendingStateUpdate(true);
    }, [log, setState, state, pendingStateUpdate]);
    const listener = useCallback((e) => {
        var _a;
        if (e.source !== ((_a = iframeRef.current) === null || _a === void 0 ? void 0 : _a.contentWindow)) {
            return;
        }
        const result = incomingMessage.decode(e.data);
        if (!isRight(result)) {
            // Don't log if the message is from iframe-resizer.
            if (typeof e.data === "string" && e.data.startsWith("[iFrameSizer]")) {
                return;
            }
            log("Invalid message from iframe", result.left, e.data);
            return;
        }
        const message = result.right;
        switch (protocolVersion) {
            case null:
                switch (message.type) {
                    case "glide:ready":
                        if (componentState !== "is-listening")
                            return;
                        log("iframe is ready", state);
                        boot(message.version);
                        break;
                    // Old event name. The "happy" path where everything is
                    // running in the iframe very fast and we have no race-condition.
                    // This results in missing theme sometimes.
                    case "start-component":
                        boot("pre-protocol");
                        break;
                }
                break;
            case "pre-protocol":
                switch (message.type) {
                    case "set-state":
                        setStateIfDifferent(message.state);
                        return;
                    case "trigger-action":
                        log("iframe setting action", message.state);
                        onActionTriggered === null || onActionTriggered === void 0 ? void 0 : onActionTriggered(message.state);
                        return;
                }
                break;
            case "v1":
            case "v2":
            case "v3":
                if (componentState !== "ready")
                    return;
                switch (message.type) {
                    case "glide:set-state":
                        setStateIfDifferent(message.state);
                        break;
                    case "glide:trigger-action":
                        log("iframe setting action", message.name);
                        onActionTriggered === null || onActionTriggered === void 0 ? void 0 : onActionTriggered(message.name);
                        break;
                }
                break;
            default:
                log(`Unknown protocol version: ${protocolVersion}`);
                break;
        }
    }, [
        boot,
        componentState,
        log,
        onActionTriggered,
        protocolVersion,
        setStateIfDifferent,
        state
    ]);
    const [retryCount, setRetryCount] = useState(0);
    useInterval(() => {
        if (retryCount > MAX_READY_RETRIES) {
            // If we've retried enough times, give up and
            // assume we're on an old version of the iframe
            // code which doesn't support the protocol.
            // This is the "unhappy" path from the old implementation.
            // The iframe sent its "start-component" message before we
            // were ready. So we try to handle that here.
            boot("pre-protocol");
            return;
        }
        log("are-you-ready interval", componentState);
        setRetryCount(c => c + 1);
        if (componentState == "is-listening") {
            // Technically this is v1+, but should be stable.
            sendV1({ type: "glide:are-you-ready", debug });
        }
    }, componentState === "is-listening" ? 200 : null);
    useEffect(() => {
        if (!iframeRef.current)
            return;
        window.addEventListener("message", listener);
        if (componentState === "initial") {
            setComponentState("is-listening");
        }
        return () => {
            window.removeEventListener("message", listener);
        };
    }, [componentState, listener]);
    const prevState = usePrevious(state);
    useEffect(() => {
        if (componentState !== "ready")
            return;
        setPendingStateUpdate(false);
        if (isEqual(state, prevState))
            return;
        sendSetState(state);
    }, [componentState, sendSetState, state, prevState]);
    useEffect(() => {
        if (componentState !== "ready")
            return;
        sendSetTheme(theme, themeV2);
    }, [componentState, sendSetTheme, theme, themeV2]);
    const prevConfig = usePrevious(config);
    useEffect(() => {
        if (componentState !== "ready")
            return;
        if (isEqual(config, prevConfig))
            return;
        sendSetConfig(config);
    }, [componentState, sendSetConfig, config, prevConfig]);
    useEffect(() => {
        if (component === undefined) {
            setSrcDoc(undefined);
            return;
        }
        if (iframeCache[component] !== undefined && !disableCache) {
            return;
        }
        try {
            fetch(makeBackendURL(host, `view-2/${component}`))
                .then(x => x.json())
                .then(json => {
                const result = View2Response.decode(json);
                if (isLeft(result)) {
                    console.error(result.left);
                    throw new Error("Invalid view-2 response");
                }
                return result.right;
            })
                .then(({ html, flags }) => {
                if (!shellHost)
                    return { html, flags };
                return {
                    html: html.replace("https://components.glide.info/shell", makeBackendURL(shellHost, "shell")),
                    flags
                };
            })
                .then(({ html, flags }) => {
                var _a;
                iframeCache[component] = {
                    content: html,
                    height: undefined,
                    showImmediately: (_a = flags === null || flags === void 0 ? void 0 : flags["show-immediately"]) !== null && _a !== void 0 ? _a : false
                };
                return html;
            })
                .then(setSrcDoc);
        }
        catch (e) {
            console.error(e);
        }
    }, [component, disableCache, host, shellHost]);
    const schema = Object.keys(state).map(id => ({
        id,
        // We don't store schema so can't bring that back from the generate step.
        // But we can use the state keys with string type as a safe fallback.
        // We just need the id field here for use inside `v1.ts > init` function.
        type: "string",
        name: id
    }));
    const baseHTML = component !== undefined
        ? (_b = (_a = iframeCache[component]) === null || _a === void 0 ? void 0 : _a.content) !== null && _b !== void 0 ? _b : srcDoc
        : undefined;
    // This ref makes sure that we have a "static" reference to the initial config.
    // This way we only change the srcDoc if the baseHTML changes, but we still send the
    // proper initialConfig when it does change.
    const initParams = React.useRef({
        fieldsDataObject: state,
        schema,
        actions,
        config: Object.assign(Object.assign({}, config), { cssVariables: convertCSSColorsToHSL(config.cssVariables) }),
        debug
    });
    initParams.current = {
        fieldsDataObject: state,
        schema,
        actions,
        config: Object.assign(Object.assign({}, config), { cssVariables: convertCSSColorsToHSL(config.cssVariables) }),
        debug
    };
    const replacedHTML = React.useMemo(() => {
        if (baseHTML === undefined) {
            return undefined;
        }
        return (baseHTML
            .replace("<html>", 
        // We need one of these possible classes to properly get the CSS variables. The init function
        // will update it if needed. But we need to know its value before hand to prevent style flashes.
        `<html class="${createHTMLClassName(initParams.current.config.colorScheme)}">`)
            // This prevents front flash of the component.
            .replace('<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap">', "")
            .replace(/init\((.*)\)/, `init(${JSON.stringify(initParams.current)})`)
            .replace("<head>", `<head>\n<style>:root {${Object.entries(initParams.current.config.cssVariables)
            .map(([key, value]) => `${key}: ${value};`)
            .join("\n")}
      }</style>`));
    }, [baseHTML]);
    React.useLayoutEffect(() => {
        if (component === undefined || iframeRef.current === null)
            return;
        iframeResize({
            license: iframeResizerLicense,
            onResized: data => {
                if (iframeCache[component] === undefined)
                    return;
                iframeCache[component].height = data.height;
            }
        }, iframeRef.current);
    }, [component]);
    if (component === undefined) {
        if (isRunning)
            return _jsx(SkeletonLoader, {});
        return null;
    }
    const heightStyle = ((_c = iframeCache[component]) === null || _c === void 0 ? void 0 : _c.height) !== undefined
        ? { height: (_d = iframeCache[component]) === null || _d === void 0 ? void 0 : _d.height }
        : {};
    const showImmediately = (_f = (_e = iframeCache[component]) === null || _e === void 0 ? void 0 : _e.showImmediately) !== null && _f !== void 0 ? _f : false;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        @keyframes pulse {
          0% {
            opacity: 0.8;
          }
          50% {
            opacity: 0.4;
          }
          100% {
            opacity: 0.8;
          }
        }
        .busy {
          animation: pulse 2s infinite;
          pointer-events: none;
        }` }), debug && (_jsxs("p", { children: [componentState, " : ", protocolVersion] })), _jsx("iframe", { id: iframeId, srcDoc: replacedHTML, ref: iframeRef, "aria-disabled": isRunning, className: [className, isRunning ? "busy" : ""]
                    .filter(Boolean)
                    .join(" "), style: Object.assign(Object.assign(Object.assign({}, (style !== null && style !== void 0 ? style : {})), heightStyle), { width: "100%", display: showImmediately || componentState === "ready" ? "block" : "none" }), sandbox: "allow-scripts allow-popups", allow: allowed.join(";") })] }));
}
